import { z } from 'zod';

/**
 * UUID validation regex
 */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Jira issue key format (e.g., PROJECT-123)
 */
const JIRA_ISSUE_KEY_REGEX = /^[A-Z][A-Z0-9]*-\d+$/;

/**
 * Schema for UUID validation
 */
export const uuidSchema = z.string().regex(UUID_REGEX, 'Invalid UUID format');

/**
 * Schema for Jira issue key validation
 */
export const jiraIssueKeySchema = z.string().regex(JIRA_ISSUE_KEY_REGEX, 'Invalid Jira issue key format');

/**
 * Schema for ISO 8601 datetime string
 */
export const isoDateTimeSchema = z.string().refine(
  (val) => !isNaN(Date.parse(val)),
  'Invalid ISO 8601 datetime format'
);

/**
 * Schema for time spent in seconds (must be positive)
 */
export const timeSpentSecondsSchema = z.number().int().min(1, 'Time spent must be at least 1 second');

/**
 * Schema for CreateIssueRequest
 */
export const createIssueRequestSchema = z.object({
  summary: z.string().min(1, 'Summary is required'),
  status: z.string().optional(),
  assigneeId: z.string().optional(),
});

/**
 * Schema for CreateTimeLogRequest
 */
export const createTimeLogRequestSchema = z.object({
  jiraId: z.string().min(1, 'Jira ID is required'),
  issueId: uuidSchema,
  authorId: z.string().min(1, 'Author ID is required'),
  startedTime: isoDateTimeSchema,
  timeSpentSeconds: timeSpentSecondsSchema,
  comment: z.string().optional(),
  labels: z.array(z.string()).optional(),
});

/**
 * Schema for UpdateTimeLogRequest
 */
export const updateTimeLogRequestSchema = z.object({
  startedTime: isoDateTimeSchema.optional(),
  timeSpentSeconds: timeSpentSecondsSchema.optional(),
  comment: z.string().optional(),
  labels: z.array(z.string()).optional(),
}).refine(
  (data) => data.startedTime !== undefined || data.timeSpentSeconds !== undefined || data.comment !== undefined || data.labels !== undefined,
  'At least one field must be provided for update'
);

/**
 * Validate and parse data with a schema, throwing a user-friendly error
 */
export function validateOrThrow<T>(schema: z.ZodSchema<T>, data: unknown): T {
  const result = schema.safeParse(data);
  if (!result.success) {
    const errorMessages = result.error.issues.map((issue) => issue.message).join(', ');
    throw new Error(`Validation error: ${errorMessages}`);
  }
  return result.data;
}

/**
 * Validate a Jira issue key for safe use in URLs
 */
export function isValidJiraIssueKey(key: string): boolean {
  return JIRA_ISSUE_KEY_REGEX.test(key);
}

/**
 * Sanitize a Jira issue key for URL usage
 * Returns null if the key is invalid
 */
export function sanitizeJiraIssueKey(key: string): string | null {
  if (!isValidJiraIssueKey(key)) {
    return null;
  }
  return encodeURIComponent(key);
}
